import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Field, FieldAttributes, Form as FormikForm, Formik } from 'formik';
import { useMutation } from 'react-query';
import moment from 'moment';
import * as Yup from 'yup';

import { InfoMessageService, TranslationService } from '../../../../../core/services';
import { DictionaryType } from '../../../../../core/types/DictionaryType';
import YupSchema, { SchemaConfig } from '../../../../../core/utils/YupSchema';
import UserProfileApi from '../../api/UserProfileApi';
import withAbortRequest, { AbortRequestPropsType } from '../../../../../core/hoc/withAbortRequest';
import { AvailableLanguagesType } from '../../types/AvailableLanguagesType';
import { CountryType } from '../../types/CountryType';
import { TenantAccountType } from '../../../../types/TenantAccountType';

type AccountSettingsTabVMType = {
  fieldLabel: string;
  fieldLength: number;
  fieldName: string;
  fieldType: string;
  fieldValue: string;
  isEditable: boolean;
  isRequired: boolean;
  isVisible: boolean;
  isVisibleOnMobile: boolean;
  order: number;
  translationKey: string;
};

type PropsType = {
  isUserFirstLoginOrUnveryfiedAccount: boolean;
  accountSettingsTabVM: AccountSettingsTabVMType[];
  availableLanguages: AvailableLanguagesType[] | undefined;
  selectedLanguage: string | undefined;
  countries: CountryType[];
  tenantDebtors: TenantAccountType[];
  isLoading: boolean;
};

const AccountSettings = (props: PropsType & AbortRequestPropsType) => {
  const history = useHistory();

  const [accountSettingsTabVM, setAccountSettingsTabVM] = useState<AccountSettingsTabVMType[]>(
    props.accountSettingsTabVM
  );
  const [languageCode, setLanguageCode] = useState<string>('');
  const [accountYepSchema, setAccountYepSchema] = useState<any>();
  const [initialAccountFormValues, setInitialAccountFormValues] =
    useState<DictionaryType<string>>();
  const [saveFeedback, setSaveFeedback] = useState<{
    severity: 'success' | 'info';
    text: string;
  } | null>(null);

  const changeUserLanguage = useMutation(
    (languageCode: string) => {
      return UserProfileApi.ChangeUserLanguage(languageCode, props.cancelTokenSource.token);
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          InfoMessageService.success(TranslationService.translate('GeneralSuccessMsg'));
        } else {
          throw data.message;
        }
        window.location.reload();
      },
      onError: (err) => {
        InfoMessageService.error(
          (err as string) || TranslationService.translate('GeneralErrorMsg')
        );
      },
    }
  );

  const changeAccountSettings = useMutation(
    (fromValues: DictionaryType<string>) => {
      return UserProfileApi.ChangeAccountSettings(fromValues, props.cancelTokenSource.token);
    },
    {
      onSuccess: (data, fromValues) => {
        if (data.success) {
          const requiresApproval = !!(data.data && (data.data as any).RequiresApproval);
          const text = data.message || TranslationService.translate('GeneralSuccessMsg');
          setSaveFeedback({
            severity: requiresApproval ? 'info' : 'success',
            text,
          });

          const accountSettings = accountSettingsTabVM.map((setting) => {
            if (Object.prototype.hasOwnProperty.call(fromValues, setting.fieldName)) {
              setting.fieldValue = fromValues[setting.fieldName];
            }
            return setting;
          });

          if (accountSettings) {
            setAccountSettingsTabVM(accountSettings);
          }
        } else {
          throw data.message;
        }
      },
      onError: (err) => {
        InfoMessageService.error(
          (err as string) || TranslationService.translate('GeneralErrorMsg')
        );
      },
    }
  );

  const handleLanguageSave = () => {
    changeUserLanguage.mutate(languageCode);
  };

  useEffect(() => {
    if (props.selectedLanguage) {
      setLanguageCode(props.selectedLanguage);
    }
  }, [props.selectedLanguage]);

  useEffect(() => {
    const accountFormDataResult = getFormObjectData(props.accountSettingsTabVM);
    const nextSchema = accountFormDataResult.reduce(YupSchema.create, {});
    setAccountYepSchema(nextSchema);

    if (props.accountSettingsTabVM) {
      setInitialAccountFormValues(getInitialFormValues(props.accountSettingsTabVM));
      setAccountSettingsTabVM(props.accountSettingsTabVM);
    }
  }, [props.accountSettingsTabVM]);

  const sortedVisibleFields = useMemo(
    () =>
      [...accountSettingsTabVM]
        .filter((f) => f.isVisible)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [accountSettingsTabVM]
  );

  const isSaving = changeAccountSettings.isLoading;

  return (
    <Stack spacing={2}>
      {props.isUserFirstLoginOrUnveryfiedAccount && (
        <Alert severity="warning" variant="outlined">
          {TranslationService.translate('UpdatePersonalInfoMsg')}
        </Alert>
      )}

      {saveFeedback && (
        <Alert
          severity={saveFeedback.severity}
          variant="standard"
          onClose={() => setSaveFeedback(null)}
          sx={{ alignItems: 'center' }}
        >
          {saveFeedback.text}
        </Alert>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardHeader
          avatar={<ContactMailIcon color="primary" />}
          title={
            <Typography variant="h6" component="h2">
              {`${TranslationService.translate('Account')}${TranslationService.translate('Settings')}`}
            </Typography>
          }
          sx={{ pb: 0 }}
        />
        <CardContent>
          {initialAccountFormValues && (
            <Formik
              initialValues={initialAccountFormValues}
              validationSchema={Yup.object().shape(accountYepSchema || {})}
              onSubmit={(values) => {
                changeAccountSettings.mutate(values);
              }}
              enableReinitialize={true}
            >
              {() => (
                <FormikForm noValidate>
                  <Grid container spacing={2}>
                    {sortedVisibleFields.map((setting) => (
                      <Grid key={setting.fieldName} size={{ xs: 12, sm: 6 }}>
                        {renderField(setting, props.countries, isSaving)}
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isSaving}
                      startIcon={
                        isSaving ? <CircularProgress size={16} color="inherit" /> : <UpdateIcon />
                      }
                    >
                      {props.isUserFirstLoginOrUnveryfiedAccount
                        ? TranslationService.translate('SaveAndVerify')
                        : TranslationService.translate('Save')}
                    </Button>
                  </Box>
                </FormikForm>
              )}
            </Formik>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardHeader
              avatar={<LanguageIcon color="primary" />}
              title={
                <Typography variant="subtitle1" component="h3">
                  {TranslationService.translate('Language')}
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TextField
                select
                variant="outlined"
                id="language"
                name="language"
                label={TranslationService.translate('Language')}
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}
                disabled={changeUserLanguage.isLoading || !props.availableLanguages?.length}
              >
                {(props.availableLanguages ?? []).map((language) => (
                  <MenuItem key={language.code} value={language.code}>
                    {language.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleLanguageSave}
                  disabled={changeUserLanguage.isLoading || !languageCode}
                  startIcon={
                    changeUserLanguage.isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {TranslationService.translate('Save')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardHeader
              avatar={<PersonIcon color="primary" />}
              title={
                <Typography variant="subtitle1" component="h3">
                  {TranslationService.translate('ShowUserDataLabel')}
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Divider sx={{ mb: 2 }} />
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => history.push('GeneralUserData')}
              >
                {TranslationService.translate('ShowUserData')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Backdrop
        open={props.isLoading && !initialAccountFormValues}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Stack>
  );
};

function renderField(
  setting: AccountSettingsTabVMType,
  countries: CountryType[],
  isSaving: boolean
) {
  const disabled = !setting.isEditable || isSaving;

  if (setting.fieldType === 'date' || setting.fieldType === 'LocalDateTime') {
    return (
      <Field name={setting.fieldName}>
        {(fieldProps: FieldAttributes<any>) => {
          const value = fieldProps.field.value ? moment(fieldProps.field.value) : null;
          const errorText =
            fieldProps.meta.touched && fieldProps.meta.error
              ? String(fieldProps.meta.error)
              : undefined;
          return (
            <DatePicker
              label={setting.fieldLabel}
              value={value}
              disabled={disabled}
              onChange={(next: any) => {
                fieldProps.form.setFieldValue(
                  setting.fieldName,
                  next && next.isValid && next.isValid()
                    ? next.locale('en').format('YYYY-MM-DD')
                    : ''
                );
              }}
              slotProps={{
                textField: {
                  name: setting.fieldName,
                  fullWidth: true,
                  size: 'small',
                  variant: 'outlined',
                  required: setting.isRequired,
                  error: !!errorText,
                  helperText: errorText,
                  onBlur: () => fieldProps.form.setFieldTouched(setting.fieldName, true),
                },
              }}
            />
          );
        }}
      </Field>
    );
  }

  if (setting.fieldName === 'Country') {
    return (
      <Field name={setting.fieldName}>
        {(fieldProps: FieldAttributes<any>) => {
          const errorText =
            fieldProps.meta.touched && fieldProps.meta.error
              ? String(fieldProps.meta.error)
              : undefined;
          return (
            <TextField
              select
              variant="outlined"
              id={setting.fieldName}
              name={setting.fieldName}
              label={setting.fieldLabel}
              value={fieldProps.field.value || ''}
              disabled={disabled}
              required={setting.isRequired}
              error={!!errorText}
              helperText={errorText}
              onChange={(e) => fieldProps.form.setFieldValue(setting.fieldName, e.target.value)}
              onBlur={() => fieldProps.form.setFieldTouched(setting.fieldName, true)}
            >
              {(countries ?? []).map((country) => (
                <MenuItem key={country.countryCode} value={country.countryCode}>
                  {country.countryName}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      </Field>
    );
  }

  return (
    <Field name={setting.fieldName}>
      {(fieldProps: FieldAttributes<any>) => {
        const errorText =
          fieldProps.meta.touched && fieldProps.meta.error
            ? String(fieldProps.meta.error)
            : undefined;
        return (
          <TextField
            variant="outlined"
            id={setting.fieldName}
            name={setting.fieldName}
            label={setting.fieldLabel}
            type={setting.fieldType === 'email' ? 'email' : 'text'}
            value={fieldProps.field.value ?? ''}
            disabled={disabled}
            required={setting.isRequired}
            error={!!errorText}
            helperText={errorText}
            slotProps={
              setting.fieldLength ? { htmlInput: { maxLength: setting.fieldLength } } : undefined
            }
            onChange={(event) =>
              fieldProps.form.setFieldValue(setting.fieldName, event.target.value)
            }
            onBlur={() => fieldProps.form.setFieldTouched(setting.fieldName, true)}
          />
        );
      }}
    </Field>
  );
}

function getFormObjectData(data: AccountSettingsTabVMType[]) {
  const formData: SchemaConfig[] = [];

  for (let i = 0; i < data.length; i++) {
    const setting = data[i];
    const result: SchemaConfig = {
      name: setting.fieldName,
      validationType: 'string',
      validations: [],
    };

    if (setting.isRequired) {
      result.validations.push({
        type: 'required',
        params: ['Required'],
      });
    }

    const isDateField = setting.fieldType === 'date' || setting.fieldType === 'LocalDateTime';

    if (setting.fieldLength && !isDateField) {
      result.validations.push({
        type: 'max',
        params: [setting.fieldLength, `Max. ${setting.fieldLength} characters`],
      });
    }

    if (setting.fieldName === 'TelephoneNr' && setting.isRequired) {
      result.validations.push({
        type: 'matches',
        params: [/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/, 'Invalid phone number format'],
      });
      result.validations.push({
        type: 'min',
        params: [8, `Too short. Minimum 8 digits.`],
      });
    }

    if (setting.fieldType === 'email') {
      result.validations.push({
        type: 'email',
        params: ['Wrong value'],
      });
    }

    formData.push(result);
  }

  return formData;
}

function getInitialFormValues(accountSettingsTabVM: AccountSettingsTabVMType[]) {
  let initialValues: DictionaryType<string> = {};

  for (let i = 0; i < accountSettingsTabVM.length; i++) {
    initialValues[accountSettingsTabVM[i].fieldName] = accountSettingsTabVM[i].fieldValue || '';
  }

  return initialValues;
}

export default React.memo(withAbortRequest(AccountSettings));
